import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Check,
  Copy,
  Key,
  Loader2,
  Lock,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import type { CredencialCliente } from '@nyroh/contracts';

import { db } from '../../services/db';

/**
 * ============================================================================
 * CREDENCIAIS DO CLIENTE — o cartao do painel do operador
 * ============================================================================
 *
 * O QUE ISTO SUBSTITUI
 *
 * O cartao antigo decidia se havia acesso com `!data.username || !data.pin`, e
 * o botao "Gerar Credenciais" fazia isto, no navegador:
 *
 *     const username = client.slug.replace(/-/g, '').substring(0, 15);
 *     const pin = Math.floor(1000 + Math.random() * 9000).toString();
 *     await db.updateClientData(client.id, { ...client.data, username, pin });
 *
 * Tres coisas erradas ao mesmo tempo:
 *
 *  1. O PIN nascia de `Math.random()`, que nao e criptografico, e ia em TEXTO
 *     PURO para dentro do JSONB de dados de negocio.
 *  2. `updateClientData` traduz `data` para o corpo de `clientes.atualizar`, que
 *     nao tem campo de credencial nenhum: os dois valores eram descartados no
 *     caminho. O clique nao criava conta. O painel mostrava o PIN porque o lia
 *     do objeto em memoria; no recarregar seguinte o cartao voltava a dizer
 *     "Cliente sem acesso".
 *  3. A condicao que decidia o estado era falsa para TODO cliente: o mapeador
 *     preenche `username` e nunca preenche `pin` (ele nao existe mais fora do
 *     hash). Ou seja: mesmo quem tinha acesso aparecia como "sem acesso".
 *
 * O QUE ESTE FAZ
 *
 * Pergunta o estado a quem sabe (`clientes.credencial`) e escreve por
 * `clientes.definirCredencial`, onde a senha vira argon2id em tabela propria. A
 * senha em claro aparece UMA vez, logo depois de emitida, para o operador
 * repassar — e nao volta em consulta nenhuma depois disso.
 */

interface Props {
  clienteId: string;
  slug: string;
}

/** A senha recem-emitida. Existe so na memoria desta tela, ate fechar. */
interface SenhaEmitida {
  usuario: string;
  senha: string;
}

export const CredenciaisDoCliente: React.FC<Props> = ({ clienteId, slug }) => {
  const [credencial, setCredencial] = useState<CredencialCliente | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [emitida, setEmitida] = useState<SenhaEmitida | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);

  // Formulario de senha escolhida pelo operador.
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [novaSenha, setNovaSenha] = useState('');
  const [erroSenha, setErroSenha] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setCredencial(await db.getClientCredential(clienteId));
    } catch (e) {
      // Falha aqui nao pode virar "cliente sem acesso": era exatamente essa
      // confusao entre "nao sei" e "nao tem" que fazia o cartao mentir.
      console.error('[credenciais] nao consegui ler o estado do acesso:', e);
      setErro('Nao consegui consultar o acesso deste cliente.');
    } finally {
      setCarregando(false);
    }
  }, [clienteId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const copiar = (texto: string, qual: string) => {
    void navigator.clipboard.writeText(texto);
    setCopiado(qual);
    setTimeout(() => setCopiado(null), 1600);
  };

  const definir = async (senha?: string) => {
    setSalvando(true);
    setErroSenha(null);
    try {
      const r = await db.setClientCredential(clienteId, senha ? { senha } : {});
      setEmitida({ usuario: r.usuario, senha: r.senha });
      setMostrarFormulario(false);
      setNovaSenha('');
      await carregar();
      toast.success(
        r.precisaTrocarSenha
          ? 'Acesso criado com a senha padrao. O cliente troca no primeiro login.'
          : 'Senha redefinida.',
      );
    } catch (e) {
      const mensagem = e instanceof Error ? e.message : 'Erro ao definir o acesso.';
      setErroSenha(mensagem);
      toast.error(mensagem);
    } finally {
      setSalvando(false);
    }
  };

  const submeterSenha = (e: React.FormEvent) => {
    e.preventDefault();
    if (novaSenha.trim().length < 8) {
      setErroSenha('A senha precisa de pelo menos 8 caracteres.');
      return;
    }
    void definir(novaSenha.trim());
  };

  const temAcesso = credencial?.temAcesso ?? false;

  return (
    <div className="pt-6 border-t border-gray-100">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
            <Lock size={12} /> Credenciais do Cliente
          </label>
          <p className="text-xs text-gray-400 mt-0.5">Acesso exclusivo ao painel do cliente.</p>
        </div>
        <a
          href={`#/login/${slug}`}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 flex items-center justify-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md text-xs font-medium transition-all"
        >
          Testar Login <ArrowRight size={14} />
        </a>
      </div>

      {carregando && (
        <div className="flex items-center gap-2 text-xs text-gray-400 py-3">
          <Loader2 size={14} className="animate-spin" /> Consultando o acesso...
        </div>
      )}

      {!carregando && erro && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-red-900">{erro}</h4>
              <p className="text-xs text-red-700 mt-0.5">
                Isto nao quer dizer que ele esta sem acesso — quer dizer que nao deu para
                perguntar. Tente de novo antes de gerar uma senha nova.
              </p>
            </div>
          </div>
          <button
            onClick={() => void carregar()}
            className="shrink-0 flex items-center gap-1.5 text-xs font-medium text-red-700 hover:text-red-900"
          >
            <RefreshCw size={14} /> Tentar de novo
          </button>
        </div>
      )}

      {!carregando && !erro && (
        <div className="space-y-3">
          {!temAcesso ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-medium text-amber-900">Cliente sem acesso</h4>
                <p className="text-xs text-amber-700 mt-0.5">
                  Este cliente foi importado antes do sistema de login.
                </p>
              </div>
              <button
                onClick={() => void definir()}
                disabled={salvando}
                className="shrink-0 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md text-xs font-medium transition-all disabled:opacity-50"
              >
                {salvando ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
                Gerar Credenciais
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-gray-50/50 border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <span className="text-[10px] text-gray-400 block uppercase font-semibold mb-0.5">
                      Usuario
                    </span>
                    <span className="font-mono text-sm text-gray-900 font-medium select-all break-all">
                      {credencial?.usuario}
                    </span>
                  </div>
                  <button
                    onClick={() => copiar(credencial?.usuario ?? '', 'usuario')}
                    className="p-1.5 text-gray-400 hover:text-black rounded-md transition-colors shrink-0"
                    title="Copiar usuario"
                  >
                    {copiado === 'usuario' ? (
                      <Check size={14} className="text-green-600" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>

                {/*
                  Onde antes ficava o PIN em texto, agora fica o ESTADO.

                  Nao e uma informacao a menos: o PIN so podia ser exibido porque
                  estava gravado em texto puro, que era o problema. O que o
                  operador precisa saber e se a conta esta em uso e se ainda esta
                  com a senha de fabrica — e isso da para responder sem guardar
                  segredo nenhum.
                */}
                <div className="bg-gray-50/50 border border-gray-200 rounded-lg p-3">
                  <span className="text-[10px] text-gray-400 block uppercase font-semibold mb-0.5">
                    Senha
                  </span>
                  <span className="text-sm text-gray-900 font-medium">
                    Guardada com hash — nem a Nyroh le
                  </span>
                  <p className="text-[11px] text-gray-400 mt-1">
                    {credencial?.ultimoAcesso
                      ? `Ultimo acesso em ${new Date(credencial.ultimoAcesso).toLocaleString('pt-BR')}`
                      : 'Nunca acessou.'}
                  </p>
                </div>
              </div>

              {credencial?.precisaTrocarSenha && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <ShieldAlert size={15} className="text-blue-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-800">
                    Ainda esta com a <b>senha padrao</b>. O painel do cliente pede a troca no
                    proximo acesso — enquanto isso nao acontecer, qualquer pessoa que conheca
                    o padrao entra nesta conta.
                  </p>
                </div>
              )}

              {credencial?.bloqueadoAte && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    Conta <b>bloqueada por tentativas</b> ate{' '}
                    {new Date(credencial.bloqueadoAte).toLocaleTimeString('pt-BR')}. Redefinir a
                    senha libera na hora.
                  </p>
                </div>
              )}

              {!mostrarFormulario ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setMostrarFormulario(true)}
                    className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-md text-xs font-medium transition-all"
                  >
                    <Key size={14} /> Definir uma senha
                  </button>
                  <button
                    onClick={() => void definir()}
                    disabled={salvando}
                    className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-md text-xs font-medium transition-all disabled:opacity-50"
                  >
                    {salvando ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    Voltar para a senha padrao
                  </button>
                </div>
              ) : (
                <form onSubmit={submeterSenha} className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block">
                    Nova senha deste cliente
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      autoFocus
                      minLength={8}
                      maxLength={72}
                      placeholder="pelo menos 8 caracteres"
                      className="flex-1 font-mono text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/10"
                    />
                    <button
                      type="submit"
                      disabled={salvando}
                      className="flex items-center justify-center gap-2 bg-black hover:bg-gray-900 text-white px-4 py-2 rounded-md text-xs font-medium transition-all disabled:opacity-50"
                    >
                      {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      Salvar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMostrarFormulario(false);
                        setNovaSenha('');
                        setErroSenha(null);
                      }}
                      className="px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-900"
                    >
                      Cancelar
                    </button>
                  </div>
                  {erroSenha && <p className="text-xs text-red-600">{erroSenha}</p>}
                  <p className="text-[11px] text-gray-400">
                    A senha nao fica guardada em lugar nenhum que da para ler depois. Copie
                    agora e passe ao cliente; se perder, redefina.
                  </p>
                </form>
              )}
            </>
          )}

          {/*
            A senha em claro, uma unica vez.

            Fica fora dos cartoes de cima de proposito: ela nao e um dado do
            cliente, e um recado momentaneo para o operador copiar. Some ao
            fechar o modal, porque nao existe consulta que a traga de volta.
          */}
          {emitida && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-900 flex items-center gap-1.5">
                <Check size={14} /> Anote agora — esta senha nao aparece de novo
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <div className="bg-white border border-green-200 rounded-md p-2.5 flex items-center justify-between">
                  <div className="min-w-0">
                    <span className="text-[10px] text-green-700 block uppercase font-semibold">
                      Usuario
                    </span>
                    <span className="font-mono text-sm text-gray-900 select-all break-all">
                      {emitida.usuario}
                    </span>
                  </div>
                  <button
                    onClick={() => copiar(emitida.usuario, 'novo-usuario')}
                    className="p-1.5 text-green-700 hover:text-green-900 shrink-0"
                    title="Copiar"
                  >
                    {copiado === 'novo-usuario' ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <div className="bg-white border border-green-200 rounded-md p-2.5 flex items-center justify-between">
                  <div className="min-w-0">
                    <span className="text-[10px] text-green-700 block uppercase font-semibold">
                      Senha
                    </span>
                    <span className="font-mono text-sm text-gray-900 select-all break-all">
                      {emitida.senha}
                    </span>
                  </div>
                  <button
                    onClick={() => copiar(emitida.senha, 'nova-senha')}
                    className="p-1.5 text-green-700 hover:text-green-900 shrink-0"
                    title="Copiar"
                  >
                    {copiado === 'nova-senha' ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setEmitida(null)}
                className="mt-3 text-xs font-medium text-green-800 hover:text-green-900 underline"
              >
                Ja anotei, pode esconder
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
